import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#2D3E55] to-[#1a2634]" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Contact Us
            </h1>
            <p className="font-body text-lg text-white/80">
              We'd love to hear from you. Reach out with questions, partnership inquiries, 
              or just to say hello.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h2 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-8">
                Get In Touch
              </h2>
              
              <div className="space-y-6">
                <Card className="hover:shadow-md transition-shadow" data-testid="contact-address">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#2D3E55]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-[#2D3E55]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-[#2D3E55] mb-1">
                          Address
                        </h3>
                        <p className="font-body text-slate-600">
                          His Presence Chapel Inc.<br />
                          Cypress, Texas 77433<br />
                          United States
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow" data-testid="contact-phone">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#F15A24]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#F15A24]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-[#2D3E55] mb-1">
                          Phone
                        </h3>
                        <a 
                          href="tel:+18328878254"
                          className="font-body text-slate-600 hover:text-[#F15A24] transition-colors"
                        >
                          +1 (832) 887-8254
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow" data-testid="contact-email">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#F7B500]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#F7B500]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-[#2D3E55] mb-1">
                          Email
                        </h3>
                        <a 
                          href="mailto:info@hispresencechapel.org"
                          className="font-body text-slate-600 hover:text-[#F15A24] transition-colors"
                        >
                          info@hispresencechapel.org
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              {submitted ? (
                <Card className="shadow-lg h-full flex items-center justify-center">
                  <CardContent className="p-12 text-center">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                        <CheckCircle className="text-green-600" size={40} />
                      </div>
                      <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">
                        Message Sent!
                      </h3>
                      <p className="font-body text-slate-600 mb-6">
                        Thank you for reaching out. We'll get back to you as soon as possible.
                      </p>
                      <Button onClick={() => setSubmitted(false)} variant="outline">
                        Send Another Message
                      </Button>
                    </motion.div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-lg" data-testid="contact-form">
                  <CardContent className="p-8">
                    <h2 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-6">
                      Send Us a Message
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="full_name">Full Name *</Label>
                          <Input
                            id="full_name"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-full-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-email"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            className="mt-2"
                            data-testid="input-phone"
                          />
                        </div>
                        <div>
                          <Label htmlFor="subject">Subject *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            className="mt-2"
                            data-testid="input-subject"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="mt-2"
                          placeholder="How can we help you?"
                          data-testid="textarea-message"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-[#F15A24] hover:bg-[#d64d1e] text-white"
                        data-testid="submit-contact-btn"
                      >
                        {isSubmitting ? 'Sending...' : (
                          <>
                            <Send size={18} className="mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
